import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

const SESSION_KEY = 'admin_auth_token';
const SESSION_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

interface AuthSession {
  token: string;
  expiresAt: number;
}

const sha256 = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const generateToken = (): string => {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
};

const getStoredSession = (): AuthSession | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session: AuthSession = JSON.parse(raw);
    if (Date.now() > session.expiresAt) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
};

export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!getStoredSession());
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const session = getStoredSession();
    setIsAuthenticated(!!session);
  }, []);

  const login = useCallback(async (password: string): Promise<boolean> => {
    setIsVerifying(true);
    setError(null);

    try {
      const hash = await sha256(password);

      const { data, error: dbError } = await supabase
        .from('kitchen_config')
        .select('admin_password_hash')
        .maybeSingle();

      if (dbError) {
        if (import.meta.env.DEV) {
          console.error('[useAdminAuth] kitchen_config query failed:', dbError);
        }
        setError(
          dbError.code === 'PGRST116' || dbError.code === 'PGRST205' || dbError.code === '42P01'
            ? 'Yapılandırma tablosu bulunamadı. Supabase migration 011_kitchen_config.sql dosyasını çalıştırın.'
            : 'Bağlantı hatası. Lütfen tekrar deneyin.'
        );
        setIsVerifying(false);
        return false;
      }

      if (!data) {
        setError('Yönetici yapılandırması bulunamadı. Veritabanı kurulumunu tamamlayın.');
        setIsVerifying(false);
        return false;
      }

      if (data.admin_password_hash !== hash) {
        setError('Hatalı şifre. Lütfen tekrar deneyin.');
        setIsVerifying(false);
        return false;
      }

      const token = generateToken();
      const expiresAt = Date.now() + SESSION_DURATION_MS;
      const session: AuthSession = { token, expiresAt };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      setIsAuthenticated(true);
      setIsVerifying(false);
      return true;
    } catch {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      setIsVerifying(false);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { isAuthenticated, isVerifying, error, login, logout, clearError };
};
